// store/modules/theme.js
const state = {
  primaryColor: "#6589f8",
  currentTheme: "theme-light"
};

const getters = {
  colorPalette: state => {
    return generateColorPalette(state.primaryColor);
  }
};

const mutations = {
  SET_PRIMARY_COLOR(state, color) {
    state.primaryColor = color;
  },
  SET_CURRENT_THEME(state, theme) {
    state.currentTheme = theme;
  }
};

const actions = {
  updatePrimaryColor({ commit, getters }, color) {
    commit("SET_PRIMARY_COLOR", color || state.primaryColor);
    // 更新 CSS 变量
    updateCSSVariables(getters.colorPalette);
  },
  updateCurrentTheme({ commit }, theme) {
    commit("SET_CURRENT_THEME", theme);
    const dom = document.getElementById("newProductsPage");
    if (dom) {
      dom.className = theme;
    }
  },
  initializeTheme({ commit, getters, state }) {
    const dom = document.getElementById("newProductsPage");
    if (dom) {
      dom.className = state.currentTheme;
      // 初始化 CSS 变量
      updateCSSVariables(getters.colorPalette);
    }
  }
};

// 颜色工具函数
function hexToHSL(hex) {
  hex = hex.replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  let r = parseInt(hex.substr(0, 2), 16) / 255;
  let g = parseInt(hex.substr(2, 2), 16) / 255;
  let b = parseInt(hex.substr(4, 2), 16) / 255;

  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

function HSLToHex(h, s, l) {
  h /= 360;
  s /= 100;
  l /= 100;
  let r, g, b;

  if (s === 0) {
    r = g = b = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = x => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function lightenColor(hex, percent) {
  const hsl = hexToHSL(hex);
  // 限制最大亮度为85%，避免变成纯白色
  const maxLightness = 85;
  hsl.l = Math.min(maxLightness, hsl.l + percent);
  return HSLToHex(hsl.h, hsl.s, hsl.l);
}

function darkenColor(hex, percent) {
  const hsl = hexToHSL(hex);
  // 限制最小亮度为15%，避免变成纯黑色
  const minLightness = 15;
  hsl.l = Math.max(minLightness, hsl.l - percent);
  return HSLToHex(hsl.h, hsl.s, hsl.l);
}

function adjustHue(hex, degrees) {
  const hsl = hexToHSL(hex);
  hsl.h = (hsl.h + degrees) % 360;
  if (hsl.h < 0) hsl.h += 360;
  return HSLToHex(hsl.h, hsl.s, hsl.l);
}

// 智能亮度调整函数 - 根据基础颜色自动调整亮度范围
function smartLightenColor(hex, targetLightness) {
  const hsl = hexToHSL(hex);
  const baseLightness = hsl.l;

  // 如果基础颜色已经很亮，减少亮度增量
  if (baseLightness > 70) {
    // 对于已经很亮的颜色，限制最大亮度
    const maxLightness = Math.min(85, baseLightness + 15);
    hsl.l = Math.min(maxLightness, targetLightness);
  } else {
    // 对于较暗的颜色，正常增加亮度
    hsl.l = Math.min(85, targetLightness);
  }

  return HSLToHex(hsl.h, hsl.s, hsl.l);
}

function generateColorPalette(baseColor) {
  const palette = {};
  const baseHSL = hexToHSL(baseColor);

  // 主色系 - 使用智能亮度调整
  palette.primary = baseColor;
  palette.primaryLight10 = smartLightenColor(baseColor, baseHSL.l + 10);
  palette.primaryLight20 = smartLightenColor(baseColor, baseHSL.l + 20);
  palette.primaryLight30 = smartLightenColor(baseColor, baseHSL.l + 30);
  palette.primaryDark10 = darkenColor(baseColor, 10);
  palette.primaryDark20 = darkenColor(baseColor, 20);
  palette.primaryDark30 = darkenColor(baseColor, 30);

  // 浅色系 - 使用智能亮度调整，确保不会变成白色
  palette.light40 = smartLightenColor(baseColor, Math.min(85, baseHSL.l + 40));
  palette.light50 = smartLightenColor(baseColor, Math.min(85, baseHSL.l + 50));
  palette.light60 = smartLightenColor(baseColor, Math.min(85, baseHSL.l + 60));
  palette.light70 = smartLightenColor(baseColor, Math.min(85, baseHSL.l + 70));
  palette.light80 = smartLightenColor(baseColor, Math.min(85, baseHSL.l + 80));

  // 深色系
  palette.dark40 = darkenColor(baseColor, 40);
  palette.dark50 = darkenColor(baseColor, 50);
  palette.dark60 = darkenColor(baseColor, 60);

  // 互补色系
  const complement = adjustHue(baseColor, 180);
  palette.complement = complement;
  palette.complementLight = smartLightenColor(complement, 20);
  palette.complementDark = darkenColor(complement, 20);

  // 三色系
  const triadic1 = adjustHue(baseColor, 120);
  const triadic2 = adjustHue(baseColor, 240);
  palette.triadic1 = triadic1;
  palette.triadic2 = triadic2;
  palette.triadic1Light = smartLightenColor(triadic1, 20);
  palette.triadic2Light = smartLightenColor(triadic2, 20);

  // 类似色系
  const analogous1 = adjustHue(baseColor, 30);
  const analogous2 = adjustHue(baseColor, -30);
  palette.analogous1 = analogous1;
  palette.analogous2 = analogous2;
  palette.analogous1Light = smartLightenColor(analogous1, 20);
  palette.analogous2Light = smartLightenColor(analogous2, 20);

  return palette;
}

// 更新 CSS 变量
function updateCSSVariables(palette) {
  const root = document.getElementById("newProductsPage");
  if (root) {
    // 设置所有颜色为 CSS 变量
    Object.keys(palette).forEach(key => {
      const cssVarName = `--color-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}`;
      root.style.setProperty(cssVarName, palette[key]);
    });
  }
  // updateElementUIColor(state.primaryColor);
  updateElementUITheme(state.primaryColor);
}
//同步更新element UI的主题色
function updateElementUIColor(primaryColor) {
  const version = require("element-ui/package.json").version;
  const url = `https://unpkg.com/element-ui@${version}/lib/theme-chalk/index.css`;
  // changeTheme(url, primaryColor);
  updateElementUITheme(primaryColor);
}
async function changeTheme(url, primaryColor) {
  try {
    // 1. 获取 Element UI 的原始样式文件
    const response = await fetch(url);
    const originalStyle = await response.text();

    // 2. 替换默认主题色 (#409EFF) 为用户选择的颜色
    const newStyle = originalStyle.replace(/#409EFF/gi, primaryColor);

    // 3. 创建或更新样式标签
    let styleTag = document.getElementById("dynamic-element-theme");
    if (!styleTag) {
      styleTag = document.createElement("style");
      styleTag.id = "dynamic-element-theme";
      const root = document.getElementById("newProductsPage");
      if (root) {
        root.appendChild(styleTag);
        // document.head.appendChild(styleTag);
      }
    }

    // 4. 应用新样式
    styleTag.innerHTML = newStyle;

    // 5. 保存到本地存储
    // localStorage.setItem('element-primary-color', primaryColor);
  } catch (error) {
    console.error("主题切换失败:", error);
  }
}
// 同步更新Element UI的完整主题
function updateElementUITheme(primaryColor) {
  // 计算衍生颜色
  const derivedColors = calculateDerivedColors(primaryColor);

  // 获取根元素
  const root = document.documentElement;

  // 设置CSS变量
  root.style.setProperty("--el-color-primary", primaryColor);
  root.style.setProperty("--el-color-primary-light-3", derivedColors.light3);
  root.style.setProperty("--el-color-primary-light-5", derivedColors.light5);
  root.style.setProperty("--el-color-primary-light-7", derivedColors.light7);
  root.style.setProperty("--el-color-primary-light-9", derivedColors.light9);
  root.style.setProperty("--el-color-primary-dark-2", derivedColors.dark2);
}

// 计算衍生颜色函数
function calculateDerivedColors(primaryColor) {
  // 将hex颜色转换为RGB
  const hexToRgb = hex => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
      : null;
  };

  // RGB转hex
  const rgbToHex = (r, g, b) =>
    "#" +
    [r, g, b]
      .map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("");

  // 混合颜色
  const mix = (color1, color2, weight) => {
    const rgb1 = hexToRgb(color1);
    const rgb2 = hexToRgb(color2);

    const w = weight / 100;
    const r = Math.round(rgb1.r * w + rgb2.r * (1 - w));
    const g = Math.round(rgb1.g * w + rgb2.g * (1 - w));
    const b = Math.round(rgb1.b * w + rgb2.b * (1 - w));

    return rgbToHex(r, g, b);
  };

  return {
    light3: mix(primaryColor, "#ffffff", 30),
    light5: mix(primaryColor, "#ffffff", 50),
    light7: mix(primaryColor, "#ffffff", 70),
    light9: mix(primaryColor, "#ffffff", 90),
    dark2: mix(primaryColor, "#000000", 20)
  };
}
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
